import { type components, unwrap } from '@shared/api-client/index';
import { toastApiError } from '@shared/ui/toast/toast-store';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../core/api';
import type { MembershipRole } from './members.hooks';
import { toastSeatLimitOrApiError } from './seat-limit';

export type Invitation = components['schemas']['InvitationResponse'];

export const invitationsKey = ['invitations'] as const;

/** Pending invitations for the workspace. Admin-only on the api; gate the query with `enabled`. */
export function useInvitations(enabled: boolean) {
	return useQuery({
		queryKey: invitationsKey,
		queryFn: async () => unwrap(await api.GET('/v1/invitations')),
		enabled,
	});
}

/** Invite an email into the workspace. Admin-only on the api. */
export function useInviteMember() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (input: { email: string; role: MembershipRole }) =>
			unwrap(await api.POST('/v1/invitations', { body: input })),
		// A free workspace at its seat cap gets the upgrade nudge; anything else the
		// generic error toast.
		onError: toastSeatLimitOrApiError,
		onSuccess: () => queryClient.invalidateQueries({ queryKey: invitationsKey }),
	});
}

/** Re-send a pending invitation, rotating its link. Admin-only. */
export function useResendInvitation() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (id: string) =>
			unwrap(await api.POST('/v1/invitations/{id}/resend', { params: { path: { id } } })),
		onError: toastApiError,
		onSuccess: () => queryClient.invalidateQueries({ queryKey: invitationsKey }),
	});
}

/** Revoke a pending invitation so its link stops working. Admin-only. */
export function useRevokeInvitation() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (id: string) =>
			unwrap(await api.DELETE('/v1/invitations/{id}', { params: { path: { id } } })),
		onError: toastApiError,
		onSuccess: () => queryClient.invalidateQueries({ queryKey: invitationsKey }),
	});
}
